import React, { useCallback, useState } from "react";
import "Common.css";
import "./PEConfirmPopup.css";
import {
  c4A4A4A,
  cECECEC,
  cA4A4A4,
  c7C7C7C,
  UNDEF,
  MSG_SHOW_SNACKBAR,
  SHOW_LOADING,
} from "Constants";
import {
  Package,
  PayOnCall,
  VisitBreakdownResponse,
} from "../../@types/PricingModels";
import { Div, Img, Txt } from "Components/GenericView";
import { InputField } from "Components/InputField";
import { Button } from "@material-ui/core";
import { TextDropdown } from "Components/TextDropdown";
import { PAY_IN_APP_REASONS, OTHERS } from "../../DataConstants";
import {
  capitalize,
  getCardIcon,
  isBlank,
  isObjEmpty,
  postMessage,
} from "Util/Utilities";
import { PortalDialog } from "Components/CommonComponents";
import { Close } from '@mui/icons-material';
import { IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { dateDiffHours } from "Util/DateUtils";

export type PayThemselvesMode =
  | "both"
  | "package"
  | "pay_as_you_go"
  | undefined;

interface PEConfirmPopupProps {
  breakdown?: VisitBreakdownResponse;
  isUpdate?: boolean; // is updating visits
  selectedPackage?: Package;
  firstVisitStartTime: Date;
  isConcierge: boolean;

  onPayOnCallConfirm: (payOnCall: PayOnCall) => void;
  onPayThemselves: (mode: PayThemselvesMode) => void;
  onPayRemainingWithCardContinue: () => void;
  onClose: () => void;
}

export const PEConfirmPopup: React.FC<PEConfirmPopupProps> = (props) => {
  const breakdown = props.breakdown;
  const isConcierge = props.isConcierge;
  //const isUpdate = props.isUpdate;
  const pkg = props.selectedPackage;

  const isOwnPkg = !!pkg?.purchased_id;
  const isPayg = !pkg?.save && !isOwnPkg;
  const topupRequired = !!pkg?.topup_required;
  //const additionalAmount = breakdown?.additional_amount;

  // When package have insufficient balance
  const topupBD = breakdown?.topup_package_breakdown;

  // When "Pay remaining with card" button pressed
  const payRemWithCardBD = breakdown?.pay_remaining_with_card;

  // When Pay as you go selected and no card added previously
  const paygBD = breakdown?.pay_as_you_go_breakdown;

  // When selected package is not owned by the CO previously
  const buyPkgBD = breakdown?.buy_package_breakdown;

  const allowPayThemselves = breakdown?.allow_pay_themselves;
  const allowTopup = breakdown?.allow_topup;

  const [isTopupClick, clickTopup] = useState(false);
  const [isPayRemWithCardClick, clickPayRemWithCard] = useState(false);
  const [isPayOnCallClick, clickPayOnCall] = useState(false);
  const [tokenResult, setTokenResult] = useState({} as any);
  const [payOnCall, setPayOnCall] = useState({} as PayOnCall);
  const [payOnCallReasonConfirmed, setPayOnCallReasonConfirmed] =
    useState(false);
  const [editingCard, setEditingCard] = useState(false);
  const [purchaseContainsGST, setPurchaseContainsGST] = useState<boolean>(false);
  const stripe: any = useStripe();
  const elements: any = useElements();

  const card = breakdown?.card_details;
  const haveCard = !!(card && card.ending_with);

  const showBreakdown =
    !isPayOnCallClick &&
    (isTopupClick || isPayRemWithCardClick || isPayg || !isOwnPkg);

  const bd = isTopupClick
    ? topupBD
    : isPayRemWithCardClick
      ? payRemWithCardBD
      : isPayg
        ? paygBD
        : !isOwnPkg
          ? buyPkgBD
          : UNDEF;

  const payRemainingWithCardDisabled = isObjEmpty(
    breakdown?.pay_remaining_with_card
  );
  const bookingWillUse = !isPayg && bd?.booking_will_use;

  const payThemselves = useCallback(() => {
    const mode: PayThemselvesMode = isPayRemWithCardClick
      ? "both"
      : isPayg
        ? "pay_as_you_go"
        : "package";
    props.onPayThemselves(mode);
  }, [props, isPayRemWithCardClick, isPayg]);

  const reason = payOnCall.reason;
  const reasonDetails = payOnCall.reason_details;

  const payOnCallDisabled =
    isBlank(reason) || (reason === OTHERS && isBlank(reasonDetails));
  const canConfirmPayOnCall = !!(payOnCall.stripe_token || haveCard);

  const payOnCallConfirm = useCallback(() => {
    payOnCall.is_topup_requested = isTopupClick;
    props.onPayOnCallConfirm(payOnCall);
  }, [payOnCall, props, isTopupClick]);

  const addCard = useCallback(() => {
    const cardElement = elements.getElement(CardElement);
    postMessage(SHOW_LOADING, 1); // show loading

    stripe.createToken(cardElement).then((result: any) => {

      postMessage(SHOW_LOADING, -1); //hide loading

      if (result.error) {
        postMessage(MSG_SHOW_SNACKBAR, {
          key: "card-error",
          message: result.error.message,
          duration: 2000,
          variant: "error",
        });
      } else {
        const token = result.token;
        payOnCall.stripe_token = token.id;
        setTokenResult(token.card || {});
        setEditingCard(false);
      }
    });
  }, [payOnCall, stripe, elements]);

  const renderGSTCalloutText = () => {
    if (purchaseContainsGST) {
      return <div className="pecp-gst-callout">
        <Txt fs={14}>Upon package completion, Homage will refund GST for visits that are GST exclusive under GST Laws. </Txt>
      </div>
    };
    return null;
  };

  const dialogClass = `ha-dialog ha-dialog-appear pecp-dlg ${isPayOnCallClick ? "pecp-dlg-oncall" : ""
    }`;

  const leadTimeInHrs = dateDiffHours(
    props.firstVisitStartTime,
    new Date(),
    false // to allow negative value
  );
  const payThemselvesDisabled = leadTimeInHrs < 5;

  return (
    <PortalDialog className={dialogClass}>
      <div className="pecp-dlg-cont">
        <IconButton
          className="pecp-dlg-close"
          onClick={props.onClose}
          aria-label="close"
        >
          <Close />
        </IconButton>
      </div>

      {!isPayOnCallClick && (
        <>
          <Txt className="pecp-wallet">Wallet</Txt>

          {/* Card details */}
          {card && (
            <Div h vc>
              {!isConcierge && (
                <>
                  <Img
                    src="/self-serve/saved-card_green@3x.png"
                    width={21}
                    mr={12}
                  />
                  <span className="pecp-visa-ending">
                    <b>{capitalize(card.type || "Card")}</b> Ending with{" "}
                    <b>{card.ending_with}</b>
                  </span>
                </>
              )}

              {isConcierge && (
                <>
                  <Div bg="#32bdc7" ph={7} pv={2} br={2} h hc vc>
                    <Img width={20} src="/homage_logo_white.png" />
                  </Div>
                  <span className="wa-ending-with">Homage Credit Card</span>
                </>
              )}
            </Div>
          )}

          {/* No Card Added */}
          {!card && (
            <Div h vc>
              <Img
                src="/self-serve/saved-card-grey@3x.png"
                width={21}
                mr={12}
              />
              <Txt color={cA4A4A4} fs={14}>
                Card not setup
              </Txt>
            </Div>
          )}

          {/* Topup required */}
          {topupRequired && (
            <Div h vc mt={8}>
              <Img src="/self-serve/packages@3x.png" width={21} mr={12} />
              <Img src="/self-serve/warning@3x.png" size={13} mr={6} />
              <Txt className="pecp-visa-ending">
                {(pkg?.name || "").toLowerCase()}
              </Txt>
            </Div>
          )}

          <Div height={1} bg={cECECEC} w100 mv={16} />
        </>
      )}

      {/* "Topup package" or "Pay remaining with card" buttons */}
      {topupRequired && !isTopupClick && !isPayRemWithCardClick && (
        <Div v>
          <Txt className="pecp-heading">
            Does the customer wants to top up or pay remaining with a card?
          </Txt>
          <Txt mt={14} mb={12} color={c7C7C7C} fs={16}>
            The customer wants to:
          </Txt>

          <Div h w100>
            <Div
              className={`pecp-border-btn${payRemainingWithCardDisabled ? " disabled" : ""
                }`}
              h
              vc
              onClick={
                payRemainingWithCardDisabled
                  ? UNDEF
                  : () => clickPayRemWithCard(true)
              }
            >
              <Img src="/self-serve/card@3x.png" />
              <span>Pay remaining with card</span>
            </Div>

            {allowTopup && (
              <Div
                className="pecp-green-btn"
                h
                vc
                onClick={() => clickTopup(true)}
              >
                <Img src="/self-serve/topup-dark@3x.png" />
                <span>Top Up Package</span>
              </Div>
            )}
          </Div>
        </Div>
      )}

      {/* Breakdown with two buttons "pay on call" and "pay themselves"  */}
      {showBreakdown && (
        <Div v>
          {/* Breakdown  */}
          <div className={purchaseContainsGST ? "pecp-greybox-with-gst" : "pecp-greybox"}>
            {bookingWillUse && (
              <div className="pecp-booking-will-use">
                This booking will use{" "}
                <Txt sb inline>
                  {bookingWillUse}
                </Txt>{" "}
                from the selected package
              </div>
            )}
            <span className="pecp-pb">Payment Breakdown</span>

            {(bd?.details || []).map((it, i) => {
              const gstText = it?.includes_gst ? ' (incl. GST)*' : '';

              if (it?.includes_gst && !purchaseContainsGST) {
                setPurchaseContainsGST(true);
              };

              return (
                <Div h className="pecp-bd-item" key={i}>
                  <Txt g>{it.title}</Txt>
                  <Txt>{it.price + gstText}</Txt>
                </Div>
              );
            }
            )}

            <Div h vc className="pecp-bd-item pecp-total-row">
              <Txt g>Total Amount</Txt>
              <Txt sb fs={20}>
                {bd?.total_amount}
              </Txt>
            </Div>
          </div>
          {renderGSTCalloutText()}
          {/* End Breakdown  */}

          {/* When insufficient balance and CA choose "Pay remaining with card" 
            and card was already added. Only show Continue button */}
          {topupRequired && haveCard && !isTopupClick && (
            <Div h hc>
              <Div
                className="pecp-green-btn"
                h
                hc
                vc
                maxW={220}
                onClick={props.onPayRemainingWithCardContinue}
              >
                <span>Continue</span>
              </Div>
            </Div>
          )}

          {/* Basically else for above case */}
          {!(topupRequired && haveCard && !isTopupClick) && (
            <>
              <Txt className="pecp-heading">
                How does the customer prefers to pay?
              </Txt>
              <Txt mt={14} mb={12} color={c7C7C7C} fs={16}>
                The customer wants to:
              </Txt>

              <Div h w100>
                <Div
                  className="pecp-border-btn"
                  h
                  hc
                  vc
                  onClick={() => clickPayOnCall(true)}
                >
                  <Img src="/self-serve/pay-oncall@3x.png" />
                  <span>
                    {isTopupClick && allowTopup
                      ? "Top up on Call"
                      : "Pay on Call"}
                  </span>
                </Div>

                {(allowPayThemselves || allowTopup) && (
                  <Div
                    className={
                      "pecp-green-btn" +
                      (payThemselvesDisabled ? " disabled" : "")
                    }
                    h
                    hc
                    vc
                    onClick={payThemselvesDisabled ? UNDEF : payThemselves}
                  >
                    <Img src="/self-serve/pay-web-white@3x.png" />
                    <Div v hc>
                      <span>
                        {isTopupClick && allowTopup
                          ? "Top Up themselves"
                          : "Pay themselves"}
                      </span>
                      <Txt l fs={14} mt={-5} textAlign="center">
                        (Share a link)
                      </Txt>
                    </Div>
                  </Div>
                )}
              </Div>
            </>
          )}
        </Div>
      )}

      {/* Pay on call confirm reason */}
      {isPayOnCallClick && !payOnCallReasonConfirmed && (
        <Div v w100>
          <Txt className="pecp-heading" mb={19} mt={32}>
            Please describe why the customer cannot{" "}
            {isTopupClick ? "top up" : "pay"} in the app.
          </Txt>

          <TextDropdown
            showDropdownArrow
            className="pecp-dropdown"
            inputClassName="pecp-td-inp"
            popupMaxHeight="280px"
            width="100%"
            popupWidth="100%"
            lineHeight={19}
            hideFocusBar
            items={PAY_IN_APP_REASONS}
            placeholder="Select a reason"
            selectedItem={payOnCall.reason}
            onChange={(_: any, txt: any) => {
              payOnCall.reason = txt || "";
              setPayOnCall({ ...payOnCall });
            }}
          />

          {payOnCall.reason === OTHERS && (
            <InputField
              type="text"
              className="pecp-other-reason"
              noAutoSelect
              lineHeight={19}
              width="100%"
              blurOnTabKey
              hideFocusBar
              placeholder="State reason"
              onTextChange={(_: any, t: string) => {
                payOnCall.reason_details = t || "";
                setPayOnCall({ ...payOnCall });
              }}
              text={payOnCall.reason_details}
            />
          )}

          <Button
            className="ha-btn-green pecp-confirm"
            disabled={payOnCallDisabled}
            onClick={() => setPayOnCallReasonConfirmed(true)}
          >
            Confirm
          </Button>
        </Div>
      )}
      {/* End Pay on call confirm reason */}

      {/* Pay on call Card section */}
      {payOnCallReasonConfirmed && (
        <Div v w100>
          {!canConfirmPayOnCall && (
            <Txt className="pecp-heading" mb={19} mt={32}>
              There's no card added
            </Txt>
          )}

          {canConfirmPayOnCall && (
            <div>
              <Txt fs={14} color={cA4A4A4} mb={3}>
                Total Amount
              </Txt>
              <Txt sb fs={24} color={c4A4A4A} mb={20}>
                {bd?.total_amount}
              </Txt>
            </div>
          )}

          <Txt mt={14} mb={12} color={c7C7C7C} fs={16}>
            {!canConfirmPayOnCall || editingCard
              ? "Please setup the card"
              : "Use the following card for this appointment:"}
          </Txt>

          {card && !editingCard && (
            <div className="pecp-edit-card">
              {!isConcierge && (
                <>
                  <Img
                    src={getCardIcon(tokenResult.brand || card?.type)}
                    width={28}
                    mr={12}
                  />
                  <Txt g>
                    Ending with{" "}
                    <b>{tokenResult.last4 || card?.ending_with || ""}</b>
                  </Txt>
                </>
              )}
              {isConcierge && (
                <>
                  <Div bg="#32bdc7" ph={7} pv={2} br={2} h hc vc>
                    <Img width={20} src="/homage_logo_white.png" />
                  </Div>
                  <span className="wa-ending-with">Homage Credit Card</span>
                </>
              )}

              <Img
                src="/self-serve/pencil-edit@3x.png"
                className="pecp-edit-ico"
                onClick={() => setEditingCard(true)}
              />
            </div>
          )}

          {(!card || editingCard) && <CardElement className="pecp-stripe" />}

          <Div h w100>
            <Div
              className="pecp-border-btn"
              h
              vc
              hc
              onClick={() => {
                setPayOnCallReasonConfirmed(false);
                setEditingCard(false);
              }}
            >
              <span>Back</span>
            </Div>

            <Div
              className="pecp-green-btn"
              h
              vc
              hc
              onClick={
                canConfirmPayOnCall && !editingCard ? payOnCallConfirm : addCard
              }
            >
              <span>
                {canConfirmPayOnCall && !editingCard ? "Confirm" : "Add Card"}
              </span>
            </Div>
          </Div>
        </Div>
      )}

      {/* End Pay on call Card section */}
    </PortalDialog>
  );
};

interface SuccessPopupProps {
  onClose: () => void;
  doneDisabled?: boolean;
  visitCount?: number;
  urgentCount?: number;
}

export const PayThemselvesSuccessPopup: React.FC<SuccessPopupProps> = ({
  onClose,
}) => {
  const dialogClass = "ha-dialog ha-dialog-appear pecp-dlg pecp-dlg-oncall";

  return (
    <PortalDialog className={dialogClass}>
      <div className="pecp-dlg-cont">
        <IconButton
          className="pecp-dlg-close"
          onClick={onClose}
          aria-label="close"
        >
          <Close />
        </IconButton>
      </div>

      <Div h mt={24}>
        <Img src="/self-serve/email-success@3x.png" size={55} mt={2} />
        <Div v ml={26}>
          <Txt sb fs={20} lineHeight="27px" color={c4A4A4A}>
            Success!
          </Txt>
          <Txt fs={16} lineHeight="22px" color={c7C7C7C} mt={2}>
            An email with the payment link has been sent to the customer.
          </Txt>
        </Div>
      </Div>
    </PortalDialog>
  );
};

export const getUrgentVisitText = function (
  visitCount?: number,
  urgentCount?: number
) {
  if (!visitCount || !urgentCount) return;
  if (visitCount === 1) {
    return "Please remind the customer that this appointment is urgent and will be fulfilled on a best effort basis.";
  } else if (urgentCount === 1) {
    return "Please remind the customer that one of the appointments is urgent and will be fulfilled on a best effort basis.";
  } else if (urgentCount > 1) {
    return `Please remind the customer that ${urgentCount} of the appointments are urgent and will be fulfilled on a best effort basis.`;
  }
};

export const MatchingSuccessPopup: React.FC<SuccessPopupProps> = ({
  onClose,
  doneDisabled,
  visitCount,
  urgentCount,
}) => {
  const urgentTxt = getUrgentVisitText(visitCount, urgentCount);

  return (
    <PortalDialog>
      <div className="vertical vdl-create-visit-dialog">
        <div className="loading-spinner-dialog">
          <CircularProgress color="primary" />
        </div>
        <div className="vdl-create-visit-dialog-text">
          Generating smart matches and inviting full matches..
          <br />
          You'll be redirected once you click the Ok Button{" "}
        </div>

        {urgentTxt && (
          <div className="pecp-matching-dlg-info">
            <Img src="/self-serve/info.png" size={18} mr={12} />
            <Txt l fs={14} color={c4A4A4A} lineHeight="20px">
              {urgentTxt}
            </Txt>
          </div>
        )}

        <Button
          color="primary"
          className="ha-btn-green vdl-create-visit-dialog-btn pecp-dlg-ok"
          variant="contained"
          disabled={doneDisabled}
          onClick={onClose}
        >
          Ok
        </Button>
      </div>
    </PortalDialog>
  );
};

export const CoUpdateErrorPopup: React.FC<any> = ({
  onClose,
  msg_type,
  err_msg,
  doneDisabled,
}) => {
  return (
    <PortalDialog>
      <Div className="vertical vdl-create-visit-dialog" margin={60} mb={0}>
        <Img src="/warning_big@3x.png" size={64} mb={30} />
        <Div fs={18} color='#4A4A4A'>{msg_type}</Div>
        <Div fs={14} color='#4A4A4A' mt={14}>{err_msg}</Div>
        <Button
          color="primary"
          className="ha-btn-green vdl-create-visit-dialog-btn pecp-dlg-ok mt-8 w-250"
          variant="contained"
          disabled={doneDisabled}
          onClick={onClose}
        >
          Ok
        </Button>
      </Div>
    </PortalDialog>
  );
};
